import React from 'react';
import { Outlet, useParams } from 'react-router-dom';
import NodeCrumbs from '../../shared/ProgramTree/NodeCrumbs';
import NodeTabs from '../../shared/ProgramTree/NodeTabs';
import useProgramPageTabs from '../../shared/ProgramTree/useProgramPageTabs';
import { useProgramSummary } from '../../shared/ProgramTree/usePrograms';
import SPAPageHeader from '../../shared/SPAPageHeader';
import { lessonGuidesPath, programLessonsPath, useContentMount } from '../contentMount';
import ProgramNavRail from '../ProgramLessons/ProgramNavRail';
import { PanelSkeleton } from '../../shared/formPanel';
import SectionNav from './SectionNav';
import { useProgram } from './useProgram';

// AIDEV-NOTE: The SPA edit page for a program: the frame, with one section at a time (SectionNav)
// rendered in the Outlet from programEditRoutes, which gets the loaded program as its context. The
// legacy form (admin/programs/_form) still owns the unmigrated sections. On the legacy mount
// (admin/ProgramsPage) the in-page rail drills into the program itself, so the SPA drill is skipped
// there; the tab row renders in both, as router links or legacy anchors via RailLink.
const ProgramEditPage = () => {
  const { programId } = useParams<{ programId: string }>();
  const pid = Number(programId);
  const mount = useContentMount();
  const inSpa = mount === 'spa';
  const { color, isLoading: isLoadingTitle, title } = useProgramSummary(pid);
  const program = useProgram(pid);
  const tabPages = useProgramPageTabs(pid);

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      {inSpa && (
        <ProgramNavRail
          isLoadingTitle={isLoadingTitle}
          programColor={color}
          programId={pid}
          programTitle={title}
        />
      )}
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        <NodeCrumbs
          programId={pid}
          programTitle={title}
          toLesson={id => lessonGuidesPath(pid, id, mount)}
          toProgram={id => programLessonsPath(id, mount)}
        />
        <SPAPageHeader title="Edit Program" />
        <div className="tw-mb-5 tw-border-0 tw-border-b tw-border-solid tw-border-spa-border">
          <NodeTabs
            label="Program pages"
            pages={tabPages}
          />
        </div>
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-6 md:tw-flex-row">
          <SectionNav programId={pid} />
          <div className="tw-min-w-0 tw-flex-1">
            {program.isLoading && <PanelSkeleton />}
            {program.isError && (
              <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">
                Couldn’t load the program. Reload the page to try again.
              </p>
            )}
            {program.data && <Outlet context={program.data} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramEditPage;
